/*
 *  @providesModule CheckoutFlowModalController
 **/

import AWSManager from 'AWSManager';
import AccountInfoModal from 'AccountInfoModal';
import style from 'CheckoutFlowModalControllerStyle';
import ClientPropertyConstants from 'ClientPropertyConstants';
import CurrentUserStore from 'CurrentUserStore';
import DepositSuccessModal, {DepositSuccessModalDeposit} from 'DepositSuccessModal';
import GlobalEventEmitter from 'GlobalEventEmitter';
import {_m} from 'INTL';
import InsufficientFundsModal from 'InsufficientFundsModal';
import LoadingIndicator from 'LoadingIndicator';
import ModalNavigationController, {ModalProps} from 'ModalNavigationController';
import NativeNavigation from 'NativeNavigation';
import RESTStore from 'RESTStore';
import ReferralInsufficientFunds from 'ReferralInsufficientFunds';
import SKZConstants from 'SKZConstants';
import SKZTournament from 'SKZTournament';
import Sizes from 'Sizes';
import SyncMatchMakingController from 'SyncMatchMakingController';
import React from 'react';
import {NativeModules, Platform} from 'react-native';
import {Interstitials} from 'InterstitialConstants';
import InterstitialsManager from 'InterstitialsManager';
import DepositSuccessPopup from 'DepositSuccessPopup';
import DepositSuccessOverlayManager from 'DepositSuccessOverlayManager';
import {navigateToDailyBonusCashScreen} from '../../Utilities/Interstitials/DailyBonusNavigationUtils';
import CheckoutController from 'CheckoutController';
const ReactVCManager = NativeModules.SKZReactViewControllerManager;
const SkillzModule = NativeModules.SkillzModule;
const SKZAPIManager = NativeModules.SKZAPIManager;
const CurrencyTypes = SKZConstants.CurrencyTypes;
import Prefs from 'Preferences';
import SkillzSDK from 'SkillzSDK';
import {trackTelemetryEvent} from 'TrackTelemetryEvent';
import {TelemetryEventName} from 'TelemetryConstants';
import { InterstitialProps } from 'app/Utilities/ViewComponents/InterstitialModal/InterstitialModal.utils';

const errorTypes = {
  DEPOSIT_EXCEEDED_MAX_REJECTED_TRANSACTIONS: 'DEPOSIT_EXCEEDED_MAX_REJECTED_TRANSACTIONS',
  AEROPAY_ACH_ERROR: 'AEROPAY_ACH_ERROR',
};

export type CheckoutFlowModalErrorType = keyof typeof errorTypes;

// Exported for testing
export const MAX_SHOWER_AMOUNT = 200;
export const MIN_SHOWER_AMOUNT = 10;

const TEST_IDS = {
  DEPOSIT_BONUS_CASH_INFO_MODAL: 'deposit_bonus_cash_info_modal',
  DEPOSIT_PROMO_BONUS_CASH_INFO_MODAL: 'deposit_promo_bonus_cash_info_modal',
  DEPOSIT_ERROR_MODAL: 'deposit_error_modal',
};

class CheckoutModalController {
  checkoutParam = {
    showShower: false,
    showerAmount: 0,
    showBonusCashExplainer: false,
    newUserBalance: 0,
    offerInfo: null,
    storeInfo: null,
    interstitialType: null as string | null,
  };

  showUhOhModal(
    errorName: CheckoutFlowModalErrorType | string,
    errorMessage: string,
    completition?: () => void
  ) {
    const modalProps: ModalProps = {
      animationType: 'fade',
      transparent: true,
    };

    const hideContact = errorName !== errorTypes.DEPOSIT_EXCEEDED_MAX_REJECTED_TRANSACTIONS;

    if (hideContact) {
      AWSManager.trackEvent('CHECKOUT_V2_FRIENDLY_FAILED_MODAL');
    } else {
      AWSManager.trackEvent('CHECKOUT_V2_BANNED_MODAL');
    }
    const modal = (
      <AccountInfoModal
        testID={TEST_IDS.DEPOSIT_ERROR_MODAL}
        titleText={_m('Uh Oh!')}
        bodyText={errorMessage}
        cancelButtonTitle={_m('CONTACT US')}
        hideCancelButton={hideContact}
        onDone={() => this._hideModal(completition)}
        onCancel={() => this._uhOhContactUs(completition)}
        fullWidthButtons={!hideContact}
        bodyStyle={style.uhOhModalBodyStyle}
        buttonStyle={
          hideContact ? style.uhOhModalSingleButtonStyle : style.uhOhModalButtonContainerStyle
        }
        titleMarginsStyle={style.uhOhModalTitleStyle}
      />
    );

    ModalNavigationController.showModal(modal, modalProps);
  }

  showInfoModal(isPromoBonusCash: boolean) {
    const modalProps: ModalProps = {
      animationType: 'fade',
      transparent: true,
    };

    if (isPromoBonusCash) {
      AWSManager.trackEvent('CHECKOUT_V2_PROMO_BONUS_CASH_INFO');
      const modal = (
        <AccountInfoModal
          testID={TEST_IDS.DEPOSIT_PROMO_BONUS_CASH_INFO_MODAL}
          titleText={_m('Promo Bonus Cash')}
          bodyText={_m(
            'This is your Bonus Cash reward for inputting a promo code. Bonus Cash can be used to pay entry fees and compete for prizes, but cannot be withdrawn.'
          )}
          cancelButtonTitle={_m('LEARN MORE')}
          onDone={this._hideModal}
          onCancel={() => this._infoModalLearnMore(isPromoBonusCash)}
          fullWidthButtons={true}
          titleMarginsStyle={style.infoModalTitleMarginStyle}
          bodyStyle={style.infoModalBodyStyle}
        />
      );
      ModalNavigationController.showModal(modal, modalProps);
    } else {
      AWSManager.trackEvent('CHECKOUT_V2_BONUS_CASH_INFO');
      const modal = (
        <AccountInfoModal
          testID={TEST_IDS.DEPOSIT_BONUS_CASH_INFO_MODAL}
          titleText={_m('Bonus Cash')}
          bodyText={_m(
            'Bonus Cash can be used to pay entry fees and compete for prizes, but cannot be withdrawn.'
          )}
          cancelButtonTitle={_m('LEARN MORE')}
          onDone={this._hideModal}
          onCancel={() => this._infoModalLearnMore(isPromoBonusCash)}
          fullWidthButtons={true}
          titleMarginsStyle={style.infoModalTitleMarginStyle}
          bodyStyle={style.infoModalBodyStyle}
        />
      );
      ModalNavigationController.showModal(modal, modalProps);
    }
  }

  async showPaymentSuccessModal(
    depositAmount: number,
    bonusCash: number,
    promoCash: number,
    newUserBalance: number,
    bankPromoPercentage?: number,
    bankPromoAmount?: number,
    promoCouponCashAmount?: number,
    ticketz?: number,
    points?: number
  ) {
    const modalProps: ModalProps = {
      animationType: 'fade',
      transparent: true,
    };

    const deposit: DepositSuccessModalDeposit = {
      depositAmount: depositAmount,
      bonusCash: bonusCash,
      promoCash: promoCash,
      bankPromoPercentage: bankPromoPercentage,
      bankPromoCash: bankPromoAmount,
      promoCouponCashAmount: promoCouponCashAmount,
      ticketz,
      points,
    };

    // Fetch user profile to get total games played
    let totalGamesPlayed = 0;
    try {
      const userProfile = await this.fetchUserProfileWithGamesPlayed(true);
      totalGamesPlayed = userProfile?.total_games_played || 0;
    } catch (error) {
      console.warn(
        'CheckoutFlowModalController: Failed to fetch user profile, using default totalGamesPlayed',
        error
      );
    }

    const depositSuccessStoreV2Enabled = ClientPropertyConstants.getStoreV2DepositSuccessEnabled();

    console.debug('CheckoutFlowModalController: showPaymentSuccessModal', {
      depositSuccessStoreV2Enabled,
      totalGamesPlayed,
      platform: Platform.OS,
      deposit,
    });

    // Store interstitial_type from current checkout config if available
    const currentCheckoutConfig = (CheckoutController as any).currentCheckoutConfig;
    const interstitialType = currentCheckoutConfig?.offer?.interstitial_type || null;
    this.checkoutParam.interstitialType = interstitialType;

    // Track payment_success event with offer details
    const offerId = currentCheckoutConfig?.offer?.id || null;
    const isInterstitialPurchase = currentCheckoutConfig?.offer?.is_interstitial_purchase || false;
    
    trackTelemetryEvent({
      event: TelemetryEventName.PAYMENT_SUCCESS,
      payload: {
        offer_type: interstitialType,
        offer_ID: offerId,
      },
    });
    
    console.debug('CheckoutFlowModalController: Tracked payment_success event', {
      offer_type: interstitialType,
      offer_ID: offerId,
      interstitialType,
      isInterstitialPurchase,
    });

    //  (this will happen when user is coming from result screen directly from an LTO) in the deposit flow
    if (depositSuccessStoreV2Enabled) {
      // Use global overlay manager to ensure modal overlays entire SDK
      // and is not affected by navigation changes
      console.debug('CheckoutFlowModalController: Calling DepositSuccessOverlayManager.show');
      DepositSuccessOverlayManager.show(
        deposit,
        () => {
          this._handleDepositSuccessDismiss();
        },
        () => {
          SkillzSDK.resetToHomeScreen();
        }
      );
    } else {
      // Legacy Deposit Success Popup
      const modal = (
        <DepositSuccessModal
          deposit={deposit}
          onDismiss={() => {
            this._handleDepositSuccessDismiss();
          }}
        />
      );
      ModalNavigationController.showModal(modal, modalProps);
    }

    this.checkoutParam.showShower = true;
    this.checkoutParam.newUserBalance = newUserBalance;
    this.checkoutParam.showerAmount = depositAmount + bonusCash + promoCash;
  }

  showAppropriateOutOfFundsModal(isCash: boolean, completion?: any) {
    this.showAppropriateOutOfFundsModalForTourament(isCash, null, completion);
  }

  showAppropriateOutOfFundsModalForTourament(
    isCash: boolean,
    tournament?: SKZTournament | null,
    completion?: any
  ) {
    if (isCash && tournament?.live_event?.id) {
      const url = 'promos/events/' + tournament.live_event.id;
      LoadingIndicator.show();
      RESTStore.getServerResponse(
        url,
        null,
        SKZAPIManager.OnError.failSilently,
        (payload: {promo_code: string} | null, _error: Error | null) => {
          LoadingIndicator.hide();
          if (payload?.promo_code) {
            this._showReferralInsufficientFundsModal(payload.promo_code);
          } else {
            this._showInsufficientFundsModal(isCash, completion);
          }
        }
      );
    } else if(!isCash && ClientPropertyConstants.getLowZBalanceLtoEnabled()) {
      const banners = InterstitialsManager.getLowZBalanceBanners();
      if (banners && banners.length > 0) {
         const bannerData = banners[0]?.data;
         if(bannerData && bannerData.redemptions_allowed)
          InterstitialsManager.showInterstitial(bannerData as InterstitialProps);
          else {
           this._showInsufficientFundsModal(isCash, completion);
          }
       }
       else {
        this._showInsufficientFundsModal(isCash, completion);
       }   
    } 
    else {
      this._showInsufficientFundsModal(isCash, completion);
    }
  }

  _showInsufficientFundsModal(isCash: boolean, completion: any) {
    const modalProps: ModalProps = {
      animationType: 'fade',
      transparent: true,
    };
    const modal = <InsufficientFundsModal isCash={isCash} completion={completion} />;
    ModalNavigationController.showModal(modal, modalProps);
  }

  _showReferralInsufficientFundsModal(code: string) {
    const modalProps: ModalProps = {
      animationType: 'fade',
      transparent: true,
    };
    const modal = <ReferralInsufficientFunds promoCode={code} />;
    ModalNavigationController.showModal(modal, modalProps);
  }

  displayCashFlying(cashAmount: number, newUserBalance: number) {
    const pagePointX = Sizes.getUIWidth() / 2;
    const pagePointY = Sizes.getUIHeight() / 2;
    const depositSource = Prefs.get('depositSourceScreen');
    GlobalEventEmitter.emit('requestBurstShowerAnimation', [
      {
        currencyType: CurrencyTypes.CASH,
        options: {
          x: pagePointX,
          y: pagePointY,
          iconCount: this._getCashShowerAmount(cashAmount),
          screenName: depositSource === 'PLOW_RESULT' ? 'result_screen' : '',
        } as any,
      },
    ]);

    Prefs.set({depositSourceScreen: null});

    // If this deposit adds more digits to the balance, wait extra time so that the count up animation doesn't happen until after the money shower is complete.
    // If the money shower starts and then the balance increases the number of digits, the money shower will go to the location of the old dollar sign and not the new one.
    // Does not apply to Android since the animation is different.
    let timeout = 0;
    if (this._newBalanceHasMoreDigits(newUserBalance, cashAmount) && Platform.OS === 'ios') {
      // eslint-disable-next-line no-magic-numbers
      timeout = 600; // semi-arbitrary value based on length of animation on native side and trial and error
    }
    setTimeout(() => {
      GlobalEventEmitter.emit('UpdateUserSummary');
    }, timeout);
  }

  _getCashShowerAmount(cashAmount: number) {
    const minAmount = Math.max(cashAmount, MIN_SHOWER_AMOUNT);
    const actualAmount = Math.min(minAmount, MAX_SHOWER_AMOUNT);
    return actualAmount;
  }

  _newBalanceHasMoreDigits(newUserBalance: number, cashAmount: number) {
    // credit to https://stackoverflow.com/a/14879700 for providing the hint for this solution
    return (
      ((Math.log10(newUserBalance) + 1) | 0) != ((Math.log10(newUserBalance - cashAmount) + 1) | 0)
    );
  }

  _infoModalLearnMore = (isPromoBonusCash: boolean) => {
    this._hideModal();
    if (isPromoBonusCash) {
      AWSManager.trackEvent('CHECKOUT_V2_PROMO_BONUS_CASH_INFO_LEARN_MORE');
    } else {
      AWSManager.trackEvent('CHECKOUT_V2_BONUS_CASH_INFO_LEARN_MORE');
    }

    if (Platform.OS == 'ios') {
      ReactVCManager.pushNativeControllerWithSelector(
        'SKZHelpCenterFAQViewController',
        'initWithFAQPageTitle:',
        ['Rewards and Loyalty Perks'],
        true,
        null,
        () => {
          /* */
        }
      );
    } else if (Platform.OS === 'android') {
      SkillzModule.showFAQWithPageTitle('Rewards and Loyalty Perks');
    }
  };

  _uhOhContactUs = (completition?: () => void) => {
    AWSManager.trackEvent('CHECKOUT_V2_CONTACT_US');
    ModalNavigationController.hideModal(completition);
    NativeNavigation.gotoDestination('ContactUs');
  };

  _handleDepositSuccessDismiss = () => {
    const interstitialType = this.checkoutParam.interstitialType;
    const isDailyBonusLTO = interstitialType === 'DAILY_BONUS';

    // Refetch active interstitials after deposit success
    InterstitialsManager.sendTrigger(Interstitials.POST_DEPOSIT);

    // If this was a daily bonus LTO deposit, redirect to DailyBonusCashScreen
    if (isDailyBonusLTO) {
      console.debug('CheckoutFlowModalController: Daily Bonus LTO deposit detected, redirecting to DailyBonusCashScreen');
      // Get user_interstitial_id from active interstitials after refetch
      // We'll need to fetch active interstitials and find the daily bonus one
      setTimeout(() => {
        InterstitialsManager.fetchBannerInterstitials()
          .then((banners) => {
            const dailyBonusBanner = banners?.find(
              (banner: any) => banner.data.interstitial_type === 'DAILY_BONUS'
            );
            const userInterstitialId = dailyBonusBanner?.data?.user_interstitial_id;
            navigateToDailyBonusCashScreen(userInterstitialId);
          })
          .catch((error) => {
            console.warn('CheckoutFlowModalController: Failed to fetch active interstitials for redirect', error);
            // Fallback: navigate without user_interstitial_id
            navigateToDailyBonusCashScreen();
          });
      }, 500); // Small delay to ensure interstitials are refetched
    } else {
      SkillzSDK.resetToHomeScreen();
    }

    this._hideModal();
  };

  _hideModal = (completition?: () => void) => {
    ModalNavigationController.hideModal(completition);

    // When the payment success modal is dismissed
    if (this.checkoutParam.showShower) {
      if (!SyncMatchMakingController.isInQueue()) {
        this.displayCashFlying(this.checkoutParam.showerAmount, this.checkoutParam.newUserBalance);
      }
      this._showIDFAPermissionPrompt();

      // Reset the values
      this.checkoutParam.newUserBalance = 0;
      this.checkoutParam.showShower = false;
      this.checkoutParam.showerAmount = 0;
      this.checkoutParam.interstitialType = null;
    }
  };

  _showIDFAPermissionPrompt = () => {
    const methodExists = typeof SkillzModule.displayIDFAPermissionPrompt === 'function';
    if (methodExists && ClientPropertyConstants.getIDFAPromptDepositEnabled()) {
      setTimeout(() => {
        SkillzModule.displayIDFAPermissionPrompt('DEPOSIT', () => {
          /* */
        });
      }, 800); // eslint-disable-line no-magic-numbers
    }
  };

  fetchUserProfileWithGamesPlayed(
    useCache: boolean = true
  ): Promise<{total_games_played?: number}> {
    return CurrentUserStore.fetchUserProfile(useCache)
      .then((userProfile: any) => {
        const totalGamesPlayed = userProfile?.total_games_played || 0;
        console.log('CheckoutFlowModalController: Total games played:', totalGamesPlayed);
        return userProfile;
      })
      .catch((error: any) => {
        console.warn('CheckoutFlowModalController: Error fetching user profile', error);
        throw error;
      });
  }
}

export default new CheckoutModalController();
